/**
 * The shape of a prompt: six sections, and everything else.
 *
 * Pure string work, deliberately kept apart from the component. It is the one
 * piece of this feature with no React, no network and no styling in it, which
 * makes it the piece worth testing — and the component cannot be imported by
 * a test, because its dependency chain reaches an ES module jest will not
 * parse.
 */

/**
 * Each box, and the heading it becomes.
 *
 * The headings must match the backend's rubric exactly. They are repeated here
 * rather than shared, because the portal cannot import from the API — but the
 * check panel is the canary: get one wrong and it immediately reports that
 * section missing, on every prompt.
 */
export const SECTIONS = [
  {
    key: 'personality',
    heading: 'Personality',
    label: 'Who the agent is',
    hint: 'Its name, its job, and how it behaves.',
    placeholder: 'You are Roger, the receptionist at My Dental Clinic. You are friendly but professional.',
    required: true,
  },
  {
    key: 'environment',
    heading: 'Environment',
    label: 'Who calls, and why',
    hint: 'That this is a phone call, who rings, and what state they are likely to be in.',
    placeholder: 'You answer the practice phone. Callers want to ask the dentist a question or book an appointment.',
    required: true,
  },
  {
    key: 'tone',
    heading: 'Tone',
    label: 'How it should sound',
    hint: 'Sentence length and register. Say it once here, not throughout.',
    placeholder: 'Speak informally, in everyday language. Keep sentences short.',
    required: true,
  },
  {
    key: 'goal',
    heading: 'Goal',
    label: 'What it should get done',
    hint: 'The steps of a call, in order — one per line.',
    placeholder: 'Greet them · Find out what they need · Offer an appointment · Take a number',
    required: true,
  },
  {
    key: 'guardrails',
    heading: 'Guardrails',
    label: 'What it must never do',
    hint: 'One rule per line. Models weight this section hardest.',
    placeholder: 'Never repeat yourself · Never give medical advice · Never discuss pricing',
    required: true,
  },
  {
    key: 'errorHandling',
    heading: 'Error handling',
    label: 'When it cannot help',
    hint: 'What to say, and after how many tries.',
    placeholder: 'If you cannot help after two tries, take a message and a number.',
    required: false,
  },
];

/**
 * Where everything that has no box of its own is kept.
 *
 * The six boxes are not the whole of a prompt. Our own website generator
 * writes fourteen headings — Company Information, Products & Services, Key
 * Policies, the common scenarios, escalation, closing — and an earlier version
 * of this parser simply dropped the nine it did not recognise, so opening a
 * generated prompt in the editor deleted the part the customer most cared
 * about. Anything unrecognised now rides along here and is written back out
 * untouched.
 */
export const EXTRA = '__extra';

/**
 * Read a prompt that already has headings back into the boxes.
 *
 * No model call: text we assembled ourselves, or anything else using the same
 * headings, can be split with a regex. Only genuinely unstructured prose needs
 * `tidy`, which costs a call and twenty seconds.
 *
 * Headings are matched without case or trailing punctuation, the same way the
 * backend's parser does, so a prompt written with `## Guardrails:` still lands.
 */
export const parseSections = (prompt) => {
  const found = emptySections();
  if (!prompt) return found;
  const byKey = SECTIONS.reduce((acc, s) => ({ ...acc, [s.heading.toLowerCase()]: s.key }), {});
  let current = null;
  const buffer = {};
  // Headings we have no box for, plus their bodies, in the order they appeared.
  const extra = [];
  let keepingExtra = false;
  for (const line of String(prompt).split('\n')) {
    const heading = line.match(/^\s{0,3}#{1,3}\s+(.+?)\s*$/);
    if (heading) {
      const name = heading[1].replace(/[:.\-\s]+$/, '').trim().toLowerCase();
      current = byKey[name] || null;
      keepingExtra = !current;
      if (current) buffer[current] = [];
      else extra.push(line);
    } else if (current) {
      buffer[current].push(line);
    } else if (keepingExtra || line.trim()) {
      // Either the body of an unknown section, or a preamble before the first
      // heading. Both are the customer's words, so both survive.
      extra.push(line);
    }
  }
  for (const [key, lines] of Object.entries(buffer)) {
    // Goal arrives numbered because we number it; the box holds bare lines.
    found[key] = lines.join('\n').trim().replace(/^\d+[.)]\s*/gm, '');
  }
  found[EXTRA] = extra.join('\n').trim();
  return found;
};

/** The headings being carried along without a box, for telling the customer. */
export const extraHeadings = (values) =>
  ((values?.[EXTRA] || '').match(/^\s{0,3}#{1,3}\s+(.+?)\s*$/gm) || [])
    .map((line) => line.replace(/^\s*#+\s*/, '').trim())
    .filter(Boolean);

/** Whether a prompt uses any heading we know, and so needs no model to split. */
export const looksStructured = (prompt) => {
  const parsed = parseSections(prompt);
  // The carrier does not count: a prompt of pure prose fills it and nothing
  // else, and that is exactly the prompt that still needs the model to sort.
  return SECTIONS.some((s) => (parsed[s.key] || '').trim());
};

export const emptySections = () =>
  SECTIONS.reduce((acc, s) => ({ ...acc, [s.key]: '' }), { [EXTRA]: '' });

/**
 * Build the prompt from the boxes.
 *
 * Numbering the goal is done here rather than asked of the customer: they
 * write one step per line and get "1." "2." in the prompt.
 */
export const assemblePrompt = (values) =>
  [
    ...SECTIONS.map((section) => {
      const body = (values[section.key] || '').trim();
      if (!body) return '';
      if (section.key === 'goal') {
        const steps = body.split('\n').map((l) => l.trim()).filter(Boolean);
        return `# ${section.heading}\n${steps.map((s, i) => `${i + 1}. ${s.replace(/^\d+[.)]\s*/, '')}`).join('\n')}`;
      }
      return `# ${section.heading}\n${body}`;
    }),
    // Last, and exactly as it arrived. Reordering someone's own sections is a
    // smaller sin than losing them, but it is still not ours to do.
    (values[EXTRA] || '').trim(),
  ]
    .filter(Boolean)
    .join('\n\n');
